import { css, styled } from 'styled-components';

import { ButtonOrLink } from '#/components/ButtonOrLink/ButtonOrLink';
import { visuallyHiddenCss } from '#/components/VisuallyHidden/VisuallyHidden';
import { colors, cssBreakpoints, shadows, transition } from '#/theme/theme';

export const dropdownFoldMenuDesktopLeftVar = '--dropdown-fold-menu-desk-left';
export const dropdownFoldMenuColumnsCountVar =
  '--dropdown-fold-menu-columns-count';
export const dropdownFoldMenuButtonItemWidthVar =
  '--dropdown-fold-menu-item-width';
export const dropdownFoldMenuButtonLinksGap = 3;

export const dropdownFoldMenuRightImageHolderClassName = 'image-holder';

export const DropdownFoldMenuHolder = styled.menu`
  ${dropdownFoldMenuButtonItemWidthVar}: 185px;

  position: absolute;
  top: 100%;
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;

  @media all and (max-width: ${cssBreakpoints.mdDown}) {
    width: 100%;
    left: 0;
    border-top: 1px solid ${colors.selectionGrey};
    z-index: 99;
  }

  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    border-radius: 8px;
    margin-top: 8px;
    box-shadow: ${shadows.toolbar};
    left: var(${dropdownFoldMenuDesktopLeftVar});
    z-index: 101;
    background: ${colors.white};
  }
`;

export const DropdownMobileBackdrop = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  height: 100vh;
  background: ${colors.calloutGrey};
  z-index: -1;

  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    display: none;
  }
`;

/*
 * Panel
 */

export const DropdownFoldMenuPanel = styled.div`
  @media all and (max-width: ${cssBreakpoints.mdDown}) {
    background: ${colors.white};
  }

  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    position: relative;
    display: flex;
    flex-direction: column;
    padding: 12px 24px 24px;
    flex-shrink: 0;
  }
`;

export const DropdownFoldMenuPanelTitle = styled.header`
  @media all and (max-width: ${cssBreakpoints.mdDown}) {
    display: none;
  }

  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    color: ${colors.lightContentGrey};
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.11em;
    margin-bottom: 22px;
  }
`;

export const DropdownFoldBottomMenuPanel = styled.div`
  @media all and (max-width: ${cssBreakpoints.mdDown}) {
    display: none;
  }
`;

/*
 * Mobile Link
 */

const dropdownFoldMenuMobileLinksHolderCss = css`
  display: flex;
  flex-direction: column;
`;

/*
 * Button Link
 */

export const DropdownFoldMenuLeftIems = styled.div`
  @media all and (max-width: ${cssBreakpoints.mdDown}) {
    ${dropdownFoldMenuMobileLinksHolderCss};
  }

  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    display: flex;
    flex-flow: column wrap;
    gap: ${dropdownFoldMenuButtonLinksGap}px;
    max-height: 240px;

    /* This width needs to be set explicitly because flex-wrap causes the browser to not calculate the container width properly */
    width: calc(
      var(${dropdownFoldMenuButtonItemWidthVar}) *
        var(${dropdownFoldMenuColumnsCountVar}) +
        ${dropdownFoldMenuButtonLinksGap}px *
        (var(${dropdownFoldMenuColumnsCountVar}) - 1)
    );
  }
`;

export const DropdownFoldMenuItem = styled(ButtonOrLink)`
  transition: ${transition};
  text-decoration: none;
  position: relative;

  @media all and (max-width: ${cssBreakpoints.mdDown}) {
    display: flex;
    align-items: center;
    gap: 10px;
    color: ${colors.mediumContentGrey};
    height: 64px;
    border-bottom: 1px solid ${colors.selectionGrey};
    font-size: 16px;
    padding-left: 32px;

    .${dropdownFoldMenuRightImageHolderClassName} {
      display: none;
    }
  }

  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    &[data-variant='left'] {
      display: inline-flex;
      align-items: center;
      height: 48px;
      width: var(${dropdownFoldMenuButtonItemWidthVar});
      background: ${colors.backgroundBlue};
      gap: 12px;
      color: ${colors.brandBlue};
      padding: 0 16px;
      line-height: 20.5px;
    }

    &[data-variant='right'] {
      border-radius: 8px;
      flex-direction: column;
      align-items: flex-start;
      gap: 8px;
      margin: -6px;
      padding: 6px;
      padding-left: 60px;
      width: 360px;
      display: inline-flex;

      .${dropdownFoldMenuRightImageHolderClassName} {
        position: absolute;
        left: 10px;
        top: 6px;

        img {
          width: 40px;
        }
      }

      &:not(:hover) .${dropdownFoldMenuRightImageHolderClassName} > img:last-of-type, &:hover .${dropdownFoldMenuRightImageHolderClassName} > img:first-of-type {
        ${visuallyHiddenCss};
      }
    }

    &:hover {
      background: ${colors.calloutGrey};
    }
  }
`;

/*
 * Right
 */

export const DropdownFoldMenuRightItems = styled.div`
  @media all and (max-width: ${cssBreakpoints.mdDown}) {
    ${dropdownFoldMenuMobileLinksHolderCss};
  }

  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    display: flex;
    flex-flow: column wrap;
    gap: 16px;
    max-height: 200px;
    width: max-content;
  }
`;

export const DropdownFoldMenuRightItemTitle = styled.div`
  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    color: ${colors.mediumContentGrey};
    font-size: 16px;
  }
`;

export const DropdownFoldMenuRightItemDescription = styled.div`
  @media all and (max-width: ${cssBreakpoints.mdDown}) {
    display: none;
  }

  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    color: ${colors.lightContentGrey};
    font-size: 14px;
    line-height: 18.5px;
  }
`;

export const DropdownFoldMenu_Box_1 = styled.div`
  display: block;
  flex-direction: row;

  @media all and (min-width: ${cssBreakpoints.mdUp}) {
    display: flex;
  }
`;
